import { useState, useEffect, useCallback } from 'react'
import { Trophy, Flame } from 'lucide-react'
import clsx from 'clsx'
import { apiUrl } from '../config/api'
import PullToRefresh from '../components/ui/PullToRefresh'
import RankBadge from '../components/ui/RankBadge'
import type { Rank } from '../types'

interface Member {
  id: number; name: string; phone: string; rank: Rank
  total_points: number; missions_completed: number; streak_days: number
}

type Period = 'week' | 'month' | 'year' | 'all'

// Gera cor consistente por nome
function getAvatarColor(name: string): string {
  const colors = ['#5865F2', '#57F287', '#FEE75C', '#EB459E', '#ED4245', '#F47FFF', '#E67E22', '#3BA55C', '#0BA5EC', '#FAA61A']
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return colors[Math.abs(hash) % colors.length]
}

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
}

export default function LeaderboardPage() {
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState<Period>('week')

  const fetchMembers = useCallback(async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(apiUrl('/v1/community/members?per_page=50'), {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })
      const data = await res.json()
      setMembers(data.data || [])
    } catch {} finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchMembers() }, [fetchMembers])

  const handleRefresh = async () => { await fetchMembers() }

  // Ordenar por pontos (por enquanto geral — quando tiver points_log por período, filtra)
  const sorted = [...members].sort((a, b) => b.total_points - a.total_points)

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="h-12 px-4 items-center border-b border-border-subtle hidden lg:flex">
        <Trophy size={20} className="text-content-muted mr-2" />
        <h1 className="text-base font-semibold text-content-normal">Ranking</h1>
      </div>

      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto w-full">
        {/* Filtros período */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {([
            { key: 'week' as Period, label: 'Semanal' },
            { key: 'month' as Period, label: 'Mensal' },
            { key: 'year' as Period, label: 'Anual' },
            { key: 'all' as Period, label: 'Geral' },
          ]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setPeriod(tab.key)}
              className={clsx(
                'px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap',
                period === tab.key
                  ? 'bg-primary text-white'
                  : 'bg-surface-secondary text-content-muted hover:bg-surface-hover',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="flex items-center gap-4 p-3 animate-pulse">
                <div className="w-8 h-4 bg-gray-200 rounded" />
                <div className="w-10 h-10 rounded-full bg-gray-200" />
                <div className="flex-1">
                  <div className="h-4 w-28 bg-gray-200 rounded mb-1" />
                  <div className="h-3 w-16 bg-gray-100 rounded" />
                </div>
                <div className="h-5 w-12 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-12">
            <Trophy size={40} className="text-content-muted mx-auto mb-3 opacity-30" />
            <p className="text-sm text-content-muted">Nenhum membro no ranking</p>
          </div>
        ) : (
          <div className="space-y-1">
            {sorted.map((member, i) => {
              const position = i + 1
              const color = getAvatarColor(member.name)
              const isTop3 = position <= 3
              const medalColors = ['#FAA61A', '#A0AEC0', '#CD7F32']

              return (
                <div
                  key={member.id}
                  className={clsx(
                    'flex items-center gap-4 px-4 py-3 rounded-xl transition-colors',
                    isTop3 ? 'bg-surface-secondary' : 'hover:bg-surface-hover',
                  )}
                >
                  {/* Posição */}
                  <span
                    className={clsx(
                      'w-7 text-center text-sm font-bold',
                      isTop3 ? '' : 'text-content-muted',
                    )}
                    style={isTop3 ? { color: medalColors[i] } : undefined}
                  >
                    {position}
                  </span>

                  {/* Avatar com cor única */}
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                    style={{ backgroundColor: color }}
                  >
                    {getInitials(member.name)}
                  </div>

                  {/* Nome + rank */}
                  <div className="flex-1 min-w-0">
                    <p className={clsx('text-sm font-medium text-content-normal truncate', isTop3 && 'font-semibold')}>
                      {member.name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <RankBadge rank={member.rank} size="sm" />
                      {member.streak_days > 0 && (
                        <span className="flex items-center gap-0.5 text-[11px] text-content-muted">
                          <Flame size={11} className="text-status-idle" />
                          {member.streak_days}d
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Pontos */}
                  <div className="text-right shrink-0">
                    <p className={clsx('text-sm font-bold', isTop3 ? 'text-primary' : 'text-content-normal')}>
                      {member.total_points}
                    </p>
                    <p className="text-[10px] text-content-muted">pts</p>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </PullToRefresh>
  )
}
